import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { merge, fromEvent, Subject, Subscription, timer } from 'rxjs';
import { startWith, switchMap, filter } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { SessionWarningComponent } from '../../shared/session-warning/session-warning.component';
import { generalRoutes } from '../routes/general-routes-list';

const INACTIVITY_MS = 4 * 60 * 1000; // show warning after 4 min of inactivity

@Injectable({ providedIn: 'root' })
export class InactivityService {
  private reset$       = new Subject<void>();
  private subscription: Subscription | null = null;
  private warningModal: NgbModalRef | null  = null;

  private activity$ = merge(
    fromEvent(document, 'mousemove'),
    fromEvent(document, 'keydown'),
    fromEvent(document, 'click'),
    fromEvent(document, 'scroll'),
    this.reset$,
  );

  constructor(
    private modalService: NgbModal,
    private authService: AuthService,
    private router: Router,
  ) {
    // Auto start/stop based on route — auth pages don't need the timer
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: any) => {
      const onAuthPage = (e.urlAfterRedirects as string).startsWith('/auth');
      if (!onAuthPage && localStorage.getItem('access_token')) {
        this.start();
      } else {
        this.stop();
      }
    });
  }

  start() {
    if (this.subscription) return; // already running
    this.subscription = this.activity$.pipe(
      startWith(null),
      switchMap(() => {
        this.closeWarning();
        return timer(INACTIVITY_MS);
      }),
    ).subscribe(() => this.openWarning());
  }

  stop() {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.closeWarning();
  }

  private openWarning() {
    if (this.warningModal) return;
    this.warningModal = this.modalService.open(SessionWarningComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });
    this.warningModal.result.then(
      (result: string) => {
        this.warningModal = null;
        result === 'stay' ? this.reset$.next() : this.logout();
      },
      () => { this.warningModal = null; }
    );
  }

  private closeWarning() {
    this.warningModal?.dismiss();
    this.warningModal = null;
  }

  private logout() {
    this.stop();
    // Capture the correct login route BEFORE localStorage is cleared.
    const loginRoute = this.getLoginRouteForCurrentUser();
    this.authService.logout().subscribe({
      next:  () => this.clearAndRedirect(loginRoute),
      error: () => this.clearAndRedirect(loginRoute),
    });
  }

  // Members must return to the member login, not the staff/funder login.
  private getLoginRouteForCurrentUser(): string {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user && user.affiliation === 'MEMBER') {
        return generalRoutes.authentication.member.login;
      }
    } catch { /* fall through to the default login */ }
    return generalRoutes.authentication.login;
  }

  private clearAndRedirect(loginRoute: string) {
    localStorage.clear();
    this.router.navigate([loginRoute]);
  }
}
